﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

//

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import Grid from "~base/Components/Layout/Grid";
import {
    buildReducerId,
    useConsilioReducer,
} from "~base/Utilities/ConsilioReducer";
import classnames from "classnames";
import { useAdditionalStyles } from "./Styles";
import { reducer, type SessionInvitePageState } from "./Store";
import { useSettings } from "~base/Settings/SettingsContext";
import Form from "~base/Components/Primitives/Form";
import SplashPage from "~base/Pages/Shared/SplashPage";
import SplashContent from "~base/Pages/Shared/SplashContent";
import { Button } from "~base/Components/Primitives/Button";
import {
    type IntlContext,
    useIntl,
} from "~base/Internationalization/IntlContext";
import {
    getSpacingClasses,
    useJssSpacingStyles,
} from "~base/Components/Layout/JssSpacing";
import { horizontalCenterPadding } from "~base/Components/Primitives/TitleContainer";
import { LabelRadioButton } from "~base/Components/Primitives/LabelRadioButton";
import { TitleSelect } from "~base/Components/Primitives/TitleSelect";
import Expander from "~base/Components/Layout/Expander";
import Container from "~base/Components/Layout/Container";
import { useGetSessionStartInfo } from "~base/Services/API/Session/GetStartInfo";
import TitleIdentifier from "~base/Components/Primitives/TitleIdentifier";
import type {
    IdentifierConfigurationInfo,
    IndividualIdentifierTypes,
    OrganizationIdentifierTypes,
} from "~base/Internationalization/ConfigurationInfo";
import * as sessionModel from "~base/Services/API/Session/Model";
import { tryFormat } from "~base/Types/Identifier";
import { useNavigateService } from "~base/Services/NavigateService";
import { useCreateSessionEnsureSubject } from "~base/Services/API/Session/CreateSession";
import { useEffect } from "react";
import { getErrorMessage } from "~base/Services/API";
import { PoweredByConsilio } from "~base/Components/Composite/PoweredByConsilio";
import UserInfoCard from "~base/Pages/Shared/UserInfoCard";
import { useGetSessions } from "~base/Services/API/Session/GetSessions";
import TextBlock from "~base/Components/Primitives/TextBlock";
import { useExtInteractionIdentifier } from "~base/Utilities/ExtIntractionIdentifier";
import { useSearchParams } from "react-router-dom";
import * as identifier from "~base/Types/Identifier";
import PromptOverlay from "~base/Components/Composite/PromptOverlay";
import { useResumeSession } from "~base/Services/API/Session/ResumeSession";
import VersionInfo from "~base/Components/Composite/VersionInfo";
import { useServerContext } from "~base/Services/ServerContext";
import { usePostAcceptInvite } from "~irad/Services/API/Invite/PostAcceptInvite";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

export const SessionInvitePage: React.FunctionComponent<unknown> = (
    props: unknown
) => {
    // Init hooks

    const intl = useIntl();
    const settings = useSettings();

    const navigate = useNavigateService();

    const [searchParams] = useSearchParams();

    const extSessionId = searchParams.get("extSessionId");
    const sessionModelFqn = searchParams.get("sessionModelFqn");
    const locale = searchParams.get("locale");
    const subjectIdentifier = searchParams.get("subjectIdentifier");
    const subjectIdentifierType = searchParams.get("subjectIdentifierType");

    // Init state

    const [state, dispatch, _] = useConsilioReducer(
        buildReducerId("SessionInvitePage"),
        reducer,
        { loading: true }
    );

    // Variables

    const serverVirtualPath = settings.valueAsString("serverVirtualPath");

    const callApi = !state.errorMessage && !state.navigateUri;

    const [loadingApi, __] = usePostAcceptInvite({
        extSessionId: extSessionId,
        sessionModelFqn: sessionModelFqn,
        locale: locale,
        subjectIdentifier: subjectIdentifier,
        subjectIdentifierType: subjectIdentifierType,
        call: callApi,
        callback: async (status, response, responseData) => {
            if (status == "Success") {
                if (response) {
                    dispatch({
                        type: "navigate",
                        replace: true,
                        uri: `/Home/Main/${responseData.sessionId}`,
                    });
                }
            } else {
                dispatch({
                    type: "fetchError",
                    message: await getErrorMessage(intl, response),
                });
            }
        },
    });

    useEffect(() => {
        if (state.navigateUri) {
            navigate.current(state.navigateUri, {
                replace: state.navigateReplace,
            });
        }
    }, [navigate, state.navigateUri, state.navigateReplace]);

    const pageLoading = state.loading || loadingApi || !!state.navigateUri;

    return (
        <SplashPage isLoading={pageLoading} showSplashScreen={false}>
            <div>
                <SplashContent constWidth={true} header={null} footer={null}>
                    <Grid templateColumns="">
                        {state.errorMessage && (
                            <Container>
                                <TextBlock
                                    styleType="Paragraph"
                                    text={state.errorMessage}
                                    styleSubType="Validation"
                                    size="Small"
                                />
                            </Container>
                        )}
                    </Grid>
                </SplashContent>
            </div>
            <div>
                <PoweredByConsilio testIdentifier="PoweredByConsilio" />
                <VersionInfo testIdentifier="SessionInvite.VersionInfo" />
            </div>
        </SplashPage>
    );
};
